import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

/**
 * Selector de proveedor con buscador.
 * - Por defecto muestra primero los proveedores del tipo `preferType` (preselección).
 * - Al escribir busca entre TODOS los proveedores sin discriminar tipo.
 */
export default function ProviderSearchSelect({
  value,
  onChange,
  providers,
  preferType = null,
  placeholder = "Sin proveedor",
  size = "default",
  testId = "provider-search-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = providers.find((p) => p.id === value);

  const { preferred, rest } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      const filtered = providers.filter((p) => {
        const haystack = `${p.nombre_sociedad || ""} ${p.nombre_comercial || ""} ${p.email || ""} ${p.ciudad || ""}`.toLowerCase();
        return haystack.includes(q);
      });
      return { preferred: [], rest: filtered.slice(0, 200) };
    }
    if (!preferType) return { preferred: [], rest: providers.slice(0, 200) };
    const pref = providers.filter((p) => p.tipo_servicio === preferType || p.tipo_servicio === "otro");
    const others = providers.filter((p) => p.tipo_servicio !== preferType && p.tipo_servicio !== "otro");
    return { preferred: pref.slice(0, 150), rest: others.slice(0, 100) };
  }, [providers, preferType, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`w-full inline-flex items-center justify-between gap-2 border rounded-md px-3 ${size === "sm" ? "h-8 text-xs" : "h-9 text-sm"} bg-white hover:bg-gray-50 border-gray-300 text-left`}
        >
          <span className="truncate text-gray-700">
            {selected ? (selected.nombre_comercial || selected.nombre_sociedad) : <span className="text-gray-400">{placeholder}</span>}
          </span>
          <ChevronsUpDown size={12} className="text-gray-400 shrink-0"/>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nombre, email o ciudad…"
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId}-input`}
          />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>No se encontraron proveedores.</CommandEmpty>
            {!query && (
              <>
                <CommandItem
                  value=""
                  onSelect={() => { onChange(""); setOpen(false); }}
                  className="text-gray-400 italic"
                >
                  (sin asignar)
                </CommandItem>
                <CommandSeparator/>
              </>
            )}
            {preferred.length > 0 && (
              <CommandGroup heading={preferType ? `Sugeridos para ${preferType}` : "Sugeridos"}>
                {preferred.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={p.id}
                    onSelect={() => { onChange(p.id); setOpen(false); }}
                  >
                    <Check size={12} className={`mr-2 ${value === p.id ? "opacity-100" : "opacity-0"}`}/>
                    <div className="flex flex-col">
                      <span>{p.nombre_comercial || p.nombre_sociedad}</span>
                      <span className="text-[10px] text-gray-400">{p.tipo_servicio} · {p.sistema_reserva}{p.ciudad ? ` · ${p.ciudad}` : ""}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {rest.length > 0 && (
              <CommandGroup heading={query ? `Resultados (${rest.length})` : "Otros proveedores"}>
                {rest.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={p.id}
                    onSelect={() => { onChange(p.id); setOpen(false); }}
                  >
                    <Check size={12} className={`mr-2 ${value === p.id ? "opacity-100" : "opacity-0"}`}/>
                    <div className="flex flex-col">
                      <span>{p.nombre_comercial || p.nombre_sociedad}</span>
                      <span className="text-[10px] text-gray-400">{p.tipo_servicio} · {p.sistema_reserva}{p.ciudad ? ` · ${p.ciudad}` : ""}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
